import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { ArrowLeft, Loader2, Ban, Search } from 'lucide-react';
import { toast } from 'sonner';

const REASON_LABELS = {
  poor_performance: 'Poor performance',
  not_a_fit: 'Not a fit',
  reliability: 'Reliability',
  conduct: 'Conduct',
  other: 'Other',
};

/**
 * Admin visibility into client → talent block relationships (active + history).
 */
const ClientTalentBlocks = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ summary: {}, blocks: [] });
  const [q, setQ] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/client-talent-blocks');
      setData(r.data || { summary: {}, blocks: [] });
    } catch {
      toast.error('Failed to load blocks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = (data.blocks || []).filter((b) => {
    if (!showInactive && !b.active) return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (b.client || '').toLowerCase().includes(s) || (b.talent || '').toLowerCase().includes(s);
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-client-blocks-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/home')} data-testid="blocks-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-xl font-bold flex items-center gap-2 text-slate-900">
            <Ban className="w-5 h-5 text-rose-500" /> Client Block Talent
          </h1>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Card><CardContent className="p-4">
            <div className="text-2xl font-bold text-rose-600" data-testid="blocks-active-count">{data.summary?.active_blocks ?? 0}</div>
            <div className="text-xs text-slate-500">Active blocks</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-2xl font-bold text-slate-700">{data.summary?.total_records ?? 0}</div>
            <div className="text-xs text-slate-500">Total records (incl. history)</div>
          </CardContent></Card>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client or talent…" className="pl-9" data-testid="blocks-search" />
          </div>
          <Button variant={showInactive ? 'default' : 'outline'} size="sm" onClick={() => setShowInactive((v) => !v)} data-testid="blocks-toggle-history">
            {showInactive ? 'Showing history' : 'Active only'}
          </Button>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>
        ) : rows.length === 0 ? (
          <Card><CardContent className="py-14 text-center text-slate-500" data-testid="blocks-empty">No blocks found.</CardContent></Card>
        ) : (
          <div className="space-y-2" data-testid="blocks-list">
            {rows.map((b) => (
              <Card key={b.id} data-testid={`admin-block-row-${b.id}`}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900">{b.client}</span>
                      <span className="text-slate-400 text-sm">blocked</span>
                      <span className="font-semibold text-slate-900">{b.talent}</span>
                      {!b.active && <Badge variant="outline" className="text-slate-500">unblocked</Badge>}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-rose-600 border-rose-200">{REASON_LABELS[b.reason_code] || b.reason_code}</Badge>
                      {b.reason_text && <span className="text-slate-400">"{b.reason_text}"</span>}
                      <span>· by {b.blocked_by || 'client'}</span>
                      <span>· {new Date(b.created_at).toLocaleDateString()}</span>
                      {b.unblocked_at && <span>· unblocked {new Date(b.unblocked_at).toLocaleDateString()}</span>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientTalentBlocks;
